import React, { useState, useRef } from 'react';
import { storage } from '../../../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Upload, Loader, X, Link as LinkIcon, ImageIcon } from 'lucide-react';

interface ImageUploaderProps {
  /** Current image URL (empty string when none). */
  value: string;
  /** Called with the new URL after upload, manual entry, or clear (''). */
  onChange: (url: string) => void;
  /** Storage folder/prefix, e.g. "content/hero". Defaults to "content/uploads". */
  pathPrefix?: string;
  /** Tailwind aspect-ratio class for the preview box. Defaults to "aspect-video". */
  aspectClass?: string;
  /** Field label shown above the control. */
  label?: string;
  /** Optional content rendered on top of the preview (e.g. headline + gradient). */
  previewOverlay?: React.ReactNode;
  /** Bubbles a human-readable error up to the parent (e.g. to show a banner). */
  onError?: (message: string) => void;
  /** Called after a successful upload (e.g. to show a success banner). */
  onUploaded?: () => void;
}

/**
 * Reusable admin image control. Upload is the primary, always-visible action
 * (click or drag-and-drop); pasting a URL is an optional fallback. Handles the
 * Firebase Storage upload internally and reports the resulting download URL via
 * onChange.
 */
const ImageUploader: React.FC<ImageUploaderProps> = ({
  value,
  onChange,
  pathPrefix = 'content/uploads',
  aspectClass = 'aspect-video',
  label,
  previewOverlay,
  onError,
  onUploaded,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError?.('Please choose an image file.');
      return;
    }
    setUploading(true);
    try {
      const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storageRef = ref(storage, `${pathPrefix}-${Date.now()}-${cleanName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      onChange(url);
      onUploaded?.();
    } catch (error) {
      console.error('Error uploading image:', error);
      onError?.('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  return (
    <div>
      {label && (
        <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
          {label}
        </label>
      )}

      {/* Preview / dropzone */}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !uploading) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={`relative ${aspectClass} w-full overflow-hidden cursor-pointer bg-gray-100 border-2 transition-colors ${
          dragOver ? 'border-brand-gold border-dashed bg-brand-gold/5' : 'border-dashed border-gray-300 hover:border-gray-400'
        }`}
        style={
          value
            ? { backgroundImage: `url(${value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : undefined
        }
      >
        {/* Optional caller-supplied overlay (headline, gradient, etc.) */}
        {previewOverlay && (
          <div className="absolute inset-0 z-10 pointer-events-none">{previewOverlay}</div>
        )}

        {/* Empty state */}
        {!value && !uploading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-gray-400 pointer-events-none px-4 text-center">
            <ImageIcon size={28} />
            <p className="mt-2 text-xs font-bold uppercase tracking-widest">Click or drag an image here</p>
            <p className="text-[10px] text-gray-400 mt-1">PNG, JPG, WEBP</p>
          </div>
        )}

        {/* Uploading state */}
        {uploading && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50 text-white">
            <Loader className="animate-spin" size={24} />
          </div>
        )}

        {/* Clear button */}
        {value && !uploading && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            aria-label="Remove image"
            className="absolute top-2 right-2 z-30 bg-black/70 text-white rounded-full p-1.5 hover:bg-black transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mt-2">
        <button
          type="button"
          onClick={() => !uploading && inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 text-xs font-heading font-bold uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
          {value ? 'Replace Image' : 'Upload Image'}
        </button>
        <button
          type="button"
          onClick={() => setShowUrl((s) => !s)}
          className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-black transition-colors"
        >
          <LinkIcon size={12} /> {showUrl ? 'Hide URL' : 'Or paste URL'}
        </button>
      </div>

      {/* Optional manual URL entry */}
      {showUrl && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-xs p-2 border border-gray-200 text-gray-500 font-mono mt-2"
          placeholder="https://..."
        />
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/*"
        onChange={(e) => {
          if (e.target.files?.[0]) upload(e.target.files[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default ImageUploader;
